/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

type UseAsyncEffectResult<T> = {
	result: T;
	error: unknown;
	isLoading: boolean;
};

/**
 * Credits: https://marmelab.com/blog/2023/01/11/use-async-effect-react.html.
 */
export function useAsyncEffect<T>(
	mountCallback: () => Promise<T>,
	unmountCallback: () => Promise<any>,
	dependencies: Array<unknown> = []
): UseAsyncEffectResult<T> {
	const isMounted = useRef( false );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ error, setError ] = useState<unknown>( undefined );
	const [ result, setResult ] = useState<any>();

	useEffect( () => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, [] );

	useEffect( () => {
		let ignore = false;
		let mountSucceeded = false;

		( async () => {
			await Promise.resolve(); // Wait for the initial cleanup in Strict mode - avoids double mutation.

			/* c8 ignore next 3 */
			if ( !isMounted.current || ignore ) {
				return;
			}

			setIsLoading( true );

			try {
				const result = await mountCallback();
				mountSucceeded = true;
				if ( isMounted.current && !ignore ) {
					setError( undefined );
					setResult( result );
					setIsLoading( false );
				} else {
					// Component was unmounted before the mount callback returned, cancel it.
					unmountCallback();
				}
			} catch ( error ) {
				/* c8 ignore next 3 */
				if ( !isMounted.current ) {
					return;
				}
				setError( error );
				setIsLoading( false );
			}
		} )();

		return () => {
			ignore = true;

			if ( mountSucceeded ) {
				unmountCallback()
					.then( () => {
						if ( !isMounted.current ) {
							return;
						}
						setResult( undefined );
					} )
					.catch( ( error: unknown ) => {
						/* c8 ignore next 3 */
						if ( !isMounted.current ) {
							return;
						}
						setError( error );
					} );
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, dependencies );

	return useMemo( () => ( { result, error, isLoading } ), [
		result,
		error,
		isLoading
	] );
}
